// src/utils/scheduleTemplates.ts

export const EVENT_TYPES = {
  TRAVEL: 'TRAVEL',
  VENUE: 'VENUE',
  STAFF: 'STAFF',
  SOUNDCHECK: 'SOUNDCHECK',
  PERFORMANCE: 'PERFORMANCE',
  LOADOUT: 'LOADOUT',
  OTHER: 'OTHER'
} as const;

export const RESIDENCY_EVENT_TYPES = {
  ...EVENT_TYPES,
  ARRIVAL: 'ARRIVAL',
  MEETING: 'MEETING', 
  WORKSHOP: 'WORKSHOP',
  REHEARSAL: 'REHEARSAL',
  COMMUNITY: 'COMMUNITY',
  SOCIAL: 'SOCIAL',
  CATERING: 'CATERING',
  ACCOMMODATION: 'ACCOMMODATION',
  CULTURAL: 'CULTURAL',
  MENTORING: 'MENTORING',
  COLLABORATION: 'COLLABORATION'
} as const;

export interface ScheduleItem {
  id: string;
  time: string;
  type: keyof typeof EVENT_TYPES | keyof typeof RESIDENCY_EVENT_TYPES;
  description: string;
  template?: string;
}

export interface TourDay {
  date: Date;
  items: ScheduleItem[];
}

export interface TemplateVariables {
  ARTIST: string;
  VENUE: string;
  VENUE_ADDRESS?: string;
  DATE?: string;
  CONTACT_NAME?: string;
  CONTACT_EMAIL?: string;
  CONTACT_PHONE?: string;
  
  // Enhanced International variables
  LOCATIONA?: string;
  LOCATIONB?: string;
  LOCATIONC?: string;
  LOCATIOND?: string;
  INBOUNDAIRLINE?: string;
  INBOUND_FLIGHT_NO?: string;
  INBOUND_BOOKING_REF?: string;
  OUTBOUNDAIRLINE?: string;
  OUTBOUND_FLIGHT_NO?: string;
  OUTBOUND_BOOKING_REF?: string;
  INBOUNDTRAIN?: string;
  INBOUND_TRAIN_NO?: string;
  OUTBOUNDTRAIN?: string;
  OUTBOUND_TRAIN_NO?: string;
  INBOUND_BUS?: string;
  INBOUND_BUS_NO?: string;
  OUTBOUND_BUS?: string;
  OUTBOUND_BUS_NO?: string;
  INBOUND_CAR?: string;
  INBOUND_CAR_NO?: string;
  OUTBOUND_CAR?: string;
  OUTBOUND_CAR_NO?: string;
  HOTEL?: string;
  ARRIVAL_DATE?: string; // e.g., '2023-10-01'
  DEPARTURE_DATE?: string; // e.g., '2023-10-05'
  
  [key: string]: string | undefined;
}

interface ResidencyVariables extends TemplateVariables {
  ARTISTIC_DIRECTOR?: string;
  TECH_MANAGER?: string;
  HOTEL_NAME?: string;
  HOTEL_ADDRESS?: string;
  RESIDENCY_DURATION?: string;
  LOCAL_ARTIST_1?: string;
  LOCAL_ARTIST_2?: string;
  LOCAL_ARTIST_3?: string;
  COMMUNITY_PARTNER_1?: string;
  COMMUNITY_PARTNER_2?: string;
  CULTURAL_SITE?: string;
  RESTAURANT?: string;
}

export interface ResidencyDay {
  dayNumber: number;
  dayTitle: string;
  items: ScheduleItem[];
}

export interface ResidencyTemplate {
  id: string;
  name: string;
  duration: number;
  days: ResidencyDay[];
}

// Single-day templates
export const getCoolerTemplate = (variables: TemplateVariables): ScheduleItem[] => [
  {
    id: '1',
    time: '15:00',
    type: 'STAFF',
    description: 'Staff Call for Event at The Cooler',
    template: 'Staff Call for Event at {VENUE}'
  },
  {
    id: '2',
    time: '16:30',
    type: 'VENUE',
    description: 'Load In Equipment and Backline',
  },
  {
    id: '3',
    time: '16:45',
    type: 'VENUE',
    description: 'Set up Equipment and Backline'
  },
  {
    id: '4',
    time: '17:00',
    type: 'SOUNDCHECK',
    description: `${variables.ARTIST} arrive at venue for Soundcheck`,
    template: '{ARTIST} arrive at venue for Soundcheck'
  },
  {
    id: '5',
    time: '17:30',
    type: 'SOUNDCHECK',
    description: 'Soundcheck Begins'
  },
  {
    id: '6',
    time: '18:30',
    type: 'SOUNDCHECK',
    description: 'Soundcheck Ends'
  },
  {
    id: '7',
    time: '19:00',
    type: 'STAFF',
    description: 'Front of House Staff Arrives'
  },
  {
    id: '8',
    time: '19:00',
    type: 'STAFF',
    description: 'Security Staff Arrives'
  },
  {
    id: '9',
    time: '19:15',
    type: 'VENUE',
    description: 'Prep Front of House for Doors'
  },
    {
    id: '12',
    time: '19:30',
    type: 'VENUE',
    description: 'Begin House Music'
  },
  {
    id: '10',
    time: '19:30',
    type: 'VENUE',
    description: 'Doors Open'
  },
  {
    id: '11',
    time: '19:45',
    type: 'VENUE',
    description: `${variables.ARTIST} to Dressing Room`,
    template: '{ARTIST} to Dressing Room'
  },
  {
    id: '13',
    time: '20:00',
    type: 'PERFORMANCE',
    description: `${variables.ARTIST} ON Stage - Set 1`,
    template: '{ARTIST} ON Stage - Set 1'
  },
  {
    id: '14',
    time: '20:45',
    type: 'PERFORMANCE',
    description: `${variables.ARTIST} OFF Stage - Set 1`,
    template: '{ARTIST} OFF Stage - Set 1'
  },
  {
    id: '15',
    time: '20:45',
    type: 'PERFORMANCE',
    description: 'Intermission'
  },
  {
    id: '16',
    time: '21:00',
    type: 'PERFORMANCE',
    description: `${variables.ARTIST} ON Stage - Set 2`,
    template: '{ARTIST} ON Stage - Set 2'
  },
  {
    id: '17',
    time: '21:45',
    type: 'PERFORMANCE',
    description: `${variables.ARTIST} OFF Stage - Set 2`,
    template: '{ARTIST} OFF Stage - Set 2'
  },
  {
    id: '18',
    time: '22:00',
    type: 'LOADOUT',
    description: 'Begin Load Out'
  },
  {
    id: '19',
    time: '22:30',
    type: 'VENUE',
    description: 'The Cooler - Venue Curfew',
    template: '{VENUE} - Venue Curfew'
  }
];

export const getDefaultVenueTemplate = (variables: TemplateVariables): ScheduleItem[] => [
  {
    id: '1',
    time: '14:00',
    type: 'STAFF',
    description: `Staff Call for Event at ${variables.VENUE}`,
    template: 'Staff Call for Event at {VENUE}'
  },
  {
    id: '2',
    time: '15:00',
    type: 'VENUE',
    description: 'Venue Access and Load In'
  },
  {
    id: '3',
    time: '15:30',
    type: 'VENUE',
    description: 'Set up Equipment and Stage'
  },
  {
    id: '4',
    time: '16:00',
    type: 'SOUNDCHECK',
    description: `${variables.ARTIST} arrive for Soundcheck`,
    template: '{ARTIST} arrive for Soundcheck'
  },
  {
    id: '5',
    time: '16:30',
    type: 'SOUNDCHECK',
    description: 'Soundcheck and Technical Setup'
  },
  {
    id: '6',
    time: '17:30',
    type: 'SOUNDCHECK',
    description: 'Soundcheck Complete'
  },
  {
    id: '7',
    time: '18:00',
    type: 'VENUE',
    description: `${variables.ARTIST} to Green Room/Backstage`,
    template: '{ARTIST} to Green Room/Backstage'
  },
  {
    id: '8',
    time: '18:30',
    type: 'STAFF',
    description: 'Front of House Team Briefing'
  },
  {
    id: '9',
    time: '19:00',
    type: 'VENUE',
    description: 'House Opens - Doors'
  },
  {
    id: '10',
    time: '19:30',
    type: 'VENUE',
    description: 'Pre-show Music Begins'
  },
  {
    id: '11',
    time: '20:00',
    type: 'PERFORMANCE',
    description: `${variables.ARTIST} Performance Begins`,
    template: '{ARTIST} Performance Begins'
  },
  {
    id: '12',
    time: '21:30',
    type: 'PERFORMANCE',
    description: `${variables.ARTIST} Performance Ends`,
    template: '{ARTIST} Performance Ends'
  },
  {
    id: '13',
    time: '21:45',
    type: 'VENUE',
    description: 'Audience Exit and Venue Clear'
  },
  {
    id: '14',
    time: '22:00',
    type: 'LOADOUT',
    description: 'Strike Equipment and Load Out'
  },
  {
    id: '15',
    time: '23:00',
    type: 'VENUE',
    description: `${variables.VENUE} - End of Hire`,
    template: '{VENUE} - End of Hire'
  }
];

export const getInternationalTemplate = (variables: TemplateVariables): ScheduleItem[] => [
  // Inbound Travel
  {
    id: 'travel-1',
    time: '18:00',
    type: 'TRAVEL',
    description: `Touring party depart ${variables.LOCATIONA || '[Origin]'} for ${variables.LOCATIONB || '[Destination]'} on ${variables.INBOUNDAIRLINE || '[Airline]'} flight ${variables.INBOUND_FLIGHT_NO || '[Flight]'}`,
    template: 'Touring party depart {LOCATIONA} for {LOCATIONB} on {INBOUNDAIRLINE} flight {INBOUND_FLIGHT_NO}'
  },
  {
    id: 'travel-2',
    time: '20:30',
    type: 'TRAVEL',
    description: `Arrive ${variables.LOCATIONB || '[Destination]'} - collect baggage and ground transport`,
    template: 'Arrive {LOCATIONB} - collect baggage and ground transport'
  },
  {
    id: 'travel-3',
    time: '21:30',
    type: 'OTHER',
    description: `Check in to ${variables.HOTEL || '[Hotel]'}`,
    template: 'Check in to {HOTEL}'
  },
  // Add venue schedule
  ...getCoolerTemplate(variables),
  // Outbound Travel (day after performance)
  {
    id: 'departure-1',
    time: '08:00',
    type: 'OTHER',
    description: `Check out of ${variables.HOTEL || '[Hotel]'}`,
    template: 'Check out of {HOTEL}'
  },
  {
    id: 'departure-2',
    time: '10:30',
    type: 'TRAVEL',
    description: `Depart ${variables.LOCATIONB || '[Current Location]'} for ${variables.LOCATIONC || '[Next Destination]'} on ${variables.OUTBOUNDAIRLINE || '[Airline]'} flight ${variables.OUTBOUND_FLIGHT_NO || '[Flight]'}`,
    template: 'Depart {LOCATIONB} for {LOCATIONC} on {OUTBOUNDAIRLINE} flight {OUTBOUND_FLIGHT_NO}'
  },
  {
    id: 'departure-3',
    time: '15:00',
    type: 'TRAVEL',
    description: `Arrive ${variables.LOCATIONC || '[Next Destination]'}`,
    template: 'Arrive {LOCATIONC}'
  }
];

export const getSmartInternationalSchedule = (variables: TemplateVariables, performanceDate: Date): TourDay[] => {
  const arrivalDate = variables.ARRIVAL_DATE ? new Date(variables.ARRIVAL_DATE) : null;
  const departureDate = variables.DEPARTURE_DATE ? new Date(variables.DEPARTURE_DATE) : null;
  
  const schedule: TourDay[] = [];
  
  // Arrival day (if different from performance date)
  if (arrivalDate && arrivalDate.toDateString() !== performanceDate.toDateString()) {
    schedule.push({
      date: arrivalDate,
      items: [
        {
          id: 'arrival-1',
          time: '11:05',
          type: 'TRAVEL',
          description: `Touring party depart ${variables.LOCATIONA || '[Origin]'} for ${variables.LOCATIONB || '[Destination]'} on ${variables.INBOUNDAIRLINE || '[Airline]'} flight ${variables.INBOUND_FLIGHT_NO || '[Flight]'}`,
          template: 'Touring party depart {LOCATIONA} for {LOCATIONB} on {INBOUNDAIRLINE} flight {INBOUND_FLIGHT_NO}'
        },
        {
          id: 'arrival-2',
          time: '15:30',
          type: 'TRAVEL',
          description: `Arrive ${variables.LOCATIONB || '[Destination]'} - collect baggage and ground transport`,
          template: 'Arrive {LOCATIONB} - collect baggage and ground transport'
        },
        {
          id: 'arrival-3',
          time: '16:30',
          type: 'ACCOMMODATION',
          description: `Check in to ${variables.HOTEL || '[Hotel]'}`,
          template: 'Check in to {HOTEL}'
        },
        {
          id: 'arrival-4',
          time: '19:00',
          type: 'OTHER',
          description: 'Rest evening before performance'
        }
      ]
    });
  }
  
  // Performance day
  const venueTemplate = variables.VENUE === 'The Cooler' ? 
    getCoolerTemplate(variables) : 
    getDefaultVenueTemplate(variables);
    
  schedule.push({
    date: performanceDate,
    items: venueTemplate
  });
  
  // Departure day (if different from performance date)
  if (departureDate && departureDate.toDateString() !== performanceDate.toDateString()) {
    schedule.push({
      date: departureDate,
      items: [
        {
          id: 'departure-1',
          time: '10:00',
          type: 'ACCOMMODATION',
          description: `Check out of ${variables.HOTEL || '[Hotel]'}`,
          template: 'Check out of {HOTEL}'
        },
        {
          id: 'departure-2',
          time: '11:30',
          type: 'TRAVEL',
          description: `Depart ${variables.LOCATIONB || '[Current Location]'} for ${variables.LOCATIONC || '[Next Destination]'} on ${variables.OUTBOUNDAIRLINE || '[Airline]'} flight ${variables.OUTBOUND_FLIGHT_NO || '[Flight]'}`,
          template: 'Depart {LOCATIONB} for {LOCATIONC} on {OUTBOUNDAIRLINE} flight {OUTBOUND_FLIGHT_NO}'
        },
        {
          id: 'departure-3',
          time: '15:00',
          type: 'TRAVEL',
          description: `Arrive ${variables.LOCATIONC || '[Next Destination]'}`,
          template: 'Arrive {LOCATIONC}'
        }
      ]
    });
  }
  
  return schedule;
};

// Multi-day residency template
export const getNavigatorResidencyTemplate = (variables: ResidencyVariables): ResidencyTemplate => {
  return {
    id: 'navigator-residency',
    name: 'Navigator Arts Centre Residency',
    duration: parseInt(variables.RESIDENCY_DURATION || '3'),
    days: [
      {
        dayNumber: 1,
        dayTitle: 'Arrival & Orientation',
        items: [
          {
            id: 'day1-1',
            time: '09:00',
            type: 'ARRIVAL',
            description: `${variables.ARTIST} arrives at Navigator Arts Centre`,
            template: '{ARTIST} arrives at Navigator Arts Centre'
          },
          {
            id: 'day1-2',
            time: '09:30',
            type: 'MEETING',
            description: `Meet with Artistic Director ${variables.ARTISTIC_DIRECTOR || '[Name TBC]'} (Office)`,
            template: 'Meet with Artistic Director {ARTISTIC_DIRECTOR} (Office)'
          },
          {
            id: 'day1-3',
            time: '10:00',
            type: 'VENUE',
            description: 'Tour of Navigator facilities and artist workspace'
          },
          {
            id: 'day1-4',
            time: '10:30',
            type: 'VENUE',
            description: `${variables.ARTIST} settles into allocated workspace/studio`,
            template: '{ARTIST} settles into allocated workspace/studio'
          },
          {
            id: 'day1-5',
            time: '11:00',
            type: 'MEETING',
            description: `Meet with Technical Manager ${variables.TECH_MANAGER || '[Name TBC]'} re: equipment/tech needs`,
            template: 'Meet with Technical Manager {TECH_MANAGER} re: equipment/tech needs'
          },
          {
            id: 'day1-6',
            time: '12:00',
            type: 'CATERING',
            description: 'Lunch with option with venue staff or local restaurant.'
          },
          {
            id: 'day1-7',
            time: '14:00',
            type: 'COMMUNITY',
            description: `Meet local artists - Group Session with ${variables.LOCAL_ARTIST_1 || '[Local Artist 1]'}, ${variables.LOCAL_ARTIST_2 || '[Local Artist 2]'}`,
            template: 'Meet local artists - Group Session with {LOCAL_ARTIST_1}, {LOCAL_ARTIST_2}'
          },
          {
            id: 'day1-8',
            time: '15:30',
            type: 'COMMUNITY',
            description: `Meet community stakeholders - ${variables.COMMUNITY_PARTNER_1 || '[Community Partner 1]'}`,
            template: 'Meet community stakeholders - {COMMUNITY_PARTNER_1}'
          },
          {
            id: 'day1-9',
            time: '17:00',
            type: 'ACCOMMODATION',
            description: `Transfer to ${variables.HOTEL_NAME || 'hotel'} accommodation`,
            template: 'Transfer to {HOTEL_NAME} accommodation'
          },
          {
            id: 'day1-10',
            time: '18:00',
            type: 'OTHER',
            description: `${variables.ARTIST} personal time / settle into hotel`,
            template: '{ARTIST} personal time / settle into hotel'
          },
          {
            id: 'day1-11',
            time: '19:30',
            type: 'SOCIAL',
            description: 'Optional welcome dinner with Navigator team'
          }
        ]
      },
      {
        dayNumber: 2,
        dayTitle: 'Creative Development',
        items: [
          {
            id: 'day2-1',
            time: '09:00',
            type: 'VENUE',
            description: `${variables.ARTIST} workspace time begins`,
            template: '{ARTIST} workspace time begins'
          },
          {
            id: 'day2-2',
            time: '10:30',
            type: 'MENTORING',
            description: `One-on-one session with ${variables.LOCAL_ARTIST_1 || '[Local Musician 1]'}`,
            template: 'One-on-one session with {LOCAL_ARTIST_1}'
          },
          {
            id: 'day2-3',
            time: '11:30',
            type: 'MENTORING',
            description: `One-on-one session with ${variables.LOCAL_ARTIST_2 || '[Local Musician 2]'}`,
            template: 'One-on-one session with {LOCAL_ARTIST_2}'
          },
          {
            id: 'day2-4',
            time: '12:30',
            type: 'CATERING',
            description: 'Lunch break'
          },
          {
            id: 'day2-5',
            time: '14:00',
            type: 'WORKSHOP',
            description: 'Group workshop with local artists'
          },
          {
            id: 'day2-6',
            time: '16:00',
            type: 'VENUE',
            description: `${variables.ARTIST} personal creative time`,
            template: '{ARTIST} personal creative time'
          },
          {
            id: 'day2-7',
            time: '17:30',
            type: 'CULTURAL',
            description: `Visit to ${variables.CULTURAL_SITE || 'local cultural site/venue'}`,
            template: 'Visit to {CULTURAL_SITE}'
          },
          {
            id: 'day2-8',
            time: '19:00',
            type: 'SOCIAL',
            description: `Dinner at ${variables.RESTAURANT || 'local restaurant'} (Navigator-organized)`,
            template: 'Dinner at {RESTAURANT} (Navigator-organized)'
          }
        ]
      },
      {
        dayNumber: 3,
        dayTitle: 'Development & Presentation',
        items: [
          {
            id: 'day3-1',
            time: '09:00',
            type: 'VENUE',
            description: `${variables.ARTIST} workspace time`,
            template: '{ARTIST} workspace time'
          },
          {
            id: 'day3-2',
            time: '11:00',
            type: 'COLLABORATION',
            description: 'Rehearsal with local collaborators'
          },
          {
            id: 'day3-3',
            time: '12:30',
            type: 'CATERING',
            description: 'Lunch break'
          },
          {
            id: 'day3-4',
            time: '14:00',
            type: 'SOUNDCHECK',
            description: 'Technical rehearsal / sound check'
          },
          {
            id: 'day3-5',
            time: '15:30',
            type: 'VENUE',
            description: `${variables.ARTIST} preparation time`,
            template: '{ARTIST} preparation time'
          },
          {
            id: 'day3-6',
            time: '16:30',
            type: 'COMMUNITY',
            description: 'Meet & greet with audience/patrons'
          },
          {
            id: 'day3-7',
            time: '17:30',
            type: 'CATERING',
            description: 'Pre-show dinner'
          },
          {
            id: 'day3-8',
            time: '19:00',
            type: 'PERFORMANCE',
            description: `${variables.ARTIST} - Public performance/presentation`,
            template: '{ARTIST} - Public performance/presentation'
          },
          {
            id: 'day3-9',
            time: '21:00',
            type: 'SOCIAL',
            description: 'Post-show reception'
          },
          {
            id: 'day3-10',
            time: '22:30',
            type: 'OTHER',
            description: 'End of residency activities'
          }
        ]
      }
    ]
  };
};